'use client';

import { Button, Heading, MediaBlock, TextPassage } from '@petsmart-ui/sparky';
import { usePathname, useSearchParams } from 'next/navigation';
import type { ReactElement } from 'react';

import { dataLayerPush } from '@libs/analytics';
import { useDecisions, useLoyaltyContext, useOptimizely, usePageLayout } from '@libs/common-next';
import { isLocaleCA } from '@libs/core';
import { handleServiceClick } from '@libs/experience-api';

import { AlertBannerContainer } from '../ValidationAlert';
import { getActivateRewardSelection } from './autoshipPointsPayoutTracking';
import { OfferCardButton } from './OfferCardButton';
import { useOfferCardActions } from './useOfferCardActions';

export function OfferCardDetails(): ReactElement | null {
    const { handleActivateOffer, selectedOffer, showSpinner } = useOfferCardActions();
    const { isLoading } = useLoyaltyContext();
    const { isAuthenticated } = usePageLayout();
    const { context } = useOptimizely();
    const isAsPointsPayoutCtaEnabled = useDecisions('as_pts_pyt_cta');
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const activateOfferIdFromUrl = searchParams?.get('activateOfferId');

    if (!selectedOffer) {
        return null;
    }

    const expirationDate = new Date(selectedOffer.availabilityEnd);
    const expirationDateStr = `Exp ${expirationDate.toLocaleDateString()}`;
    const showButton = selectedOffer.isActivatable || selectedOffer.isActivated;
    const shopButtonUrl = isLocaleCA() ? selectedOffer.webUrlCa : selectedOffer.webUrl;

    return (
        <div className="c-offer-card-details">
            <AlertBannerContainer />

            <MediaBlock>
                <MediaBlock.Header>
                    <img alt={selectedOffer.shortDescription} src={selectedOffer.webImageUrl} />
                </MediaBlock.Header>
                <MediaBlock.Body>
                    <Heading size="body-lg-bold" tagName="h2">
                        {selectedOffer.shortDescription}
                    </Heading>
                    <TextPassage size="sm">
                        <p>{expirationDateStr}</p>
                    </TextPassage>
                </MediaBlock.Body>
            </MediaBlock>
            <TextPassage className="offer-card-details-description">
                <p>{selectedOffer.longDescription}</p>
            </TextPassage>

            {showButton && (
                <OfferCardButton
                    fullWidth
                    isActivated={selectedOffer.isActivated}
                    loading={showSpinner(selectedOffer) || isLoading}
                    onClick={e => {
                        const element = e.currentTarget as HTMLElement;
                        e.stopPropagation();
                        context?.trackEvent('_optex__loyalty_fly_out_activation');
                        const siteSection = pathname?.includes('offers') ? 'offers' : 'treats rewards';

                        handleActivateOffer(selectedOffer, siteSection, 'activate');

                        element.blur();

                        if (!selectedOffer.isActivated) {
                            dataLayerPush({
                                event: 'activate_reward',
                                page_section: siteSection,
                                selection: getActivateRewardSelection({
                                    offer: selectedOffer,
                                    isAsPointsPayoutCtaEnabled,
                                    activateOfferIdFromUrl,
                                }),
                            });
                        }
                    }}
                    size="lg"
                />
            )}
            {shopButtonUrl && (
                <Button
                    className="shop-offer-btn"
                    text="Shop this offer"
                    fullWidth
                    onClick={async event => {
                        await handleServiceClick({
                            authType: isAuthenticated,
                            event,
                            link: shopButtonUrl,
                        });
                    }}
                />
            )}
        </div>
    );
}
